import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Checkbox, App, Select, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCodeByEmail, getCodeByPhone } from '@/api/user';
import styles from './index.module.scss';

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  code: string;
  password: string;
  confirmPassword: string;
}

type Timer = ReturnType<typeof setInterval>;

// 常用区号列表
const countryCodes = [
  { value: '+1', label: '+1' },
  { value: '+86', label: '+86' },
  { value: '+44', label: '+44' },
  { value: '+81', label: '+81' },
  { value: '+82', label: '+82' },
  { value: '+65', label: '+65' },
  { value: '+852', label: '+852' },
  { value: '+853', label: '+853' },
  { value: '+886', label: '+886' },
];


const Register: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // 获取表单值
  const formValues = Form.useWatch([], form);

  // 协议选中状态
  const [checked, setChecked] = useState(false);

  // 验证码倒计时
  const [countdown, setCountdown] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [timer, setTimer] = useState<Timer | null>(null);

  // 选中的区号
  const [countryCode, setCountryCode] = useState('+1');

  // 发送验证码
  const handleSendCode = () => {
    const { phone } = formValues;
    if (!phone) {
      message.error(t("common.phoneReq"));
      return;
    }

    setIsDisabled(true);

    getCodeByPhone({ 
      phone: countryCode + phone
    }).then((res: any) => {
      setIsDisabled(false);
      if (res.code === 200) {
        message.success(t('login.verficationSuccess'));
        if (countdown > 0) return;
        
        setCountdown(60);
        const t1 = setInterval(() => {
          setCountdown(prev => prev - 1);
        }, 1000);
        setTimer(t1);
      } else {
        message.error(res.message);
      }
    }).catch(err => {
      setIsDisabled(false);
      console.log(err, 'err');
    });
  };

  // 协议选中处理
  const changeCheck = (e: any) => {
    setChecked(e.target.checked);
  };

  // 注册提交
  const onFinish = async (values: RegisterForm) => {
    if (!checked) {
      message.error(t('login.agreePrivacy'));
      return;
    }

    if (values.password !== values.confirmPassword) {
      message.error(t("login.passwordNotMatch"));
      return;
    }

    try {
      // 这里应该调用注册API
      console.log('Register data:', values);
      message.success(t("login.RegistrationSuccess"));
      navigate('/login');
    } catch (error: any) {
      console.error('Registration failed:', error);
      message.error(t("login.regisFailed"));
    }
  };

  // 清空定时器逻辑
  useEffect(() => {
    if (countdown === 0 && timer) {
      clearInterval(timer);
      setTimer(null);
    }
  }, [countdown, timer]);

  return (
    <div className={styles['register-container']}>
      <div className={styles['register-card']}>
        <div className={styles['register-title']}>{t("login.Register")}</div>
        
        <Form
          form={form}
          name="register"
          className={styles['register-form']}
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
        >
          {/* 姓名行 */}
          <div className={styles['name-row']}>
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: t('login.firstNamePl') }]}
              className={styles['name-input']}
            >
              <Input placeholder={t('login.firstName')} size="large" />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[{ required: true, message: t('login.lastNamePl') }]}
              className={styles['name-input']}
            >
              <Input placeholder={t('login.lastName')} size="large" />
            </Form.Item>
          </div>

          {/* 邮箱 */}
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('login.EmailAddressPl') },
              { type: 'email', message: t('login.emailvalid') }
            ]}
          >
            <Input placeholder={t('login.EmailAddress')} size="large" />
          </Form.Item>

          {/* 手机号 */}
          <Form.Item
            name="phone"
            rules={[{ required: true, message: t('login.phonePl') }]}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={countryCode}
                onChange={setCountryCode}
                style={{ width: 80 }}
                optionFilterProp="label"
                size="large"
              >
                {countryCodes.map(country => (
                  <Select.Option key={country.value} value={country.value}>
                    {country.value}
                  </Select.Option>
                ))}
              </Select>
              <Input
                placeholder={t('login.phone')}
                size="large"
                style={{ flex: 1 }}
              />
            </Space.Compact>
          </Form.Item>

          {/* 验证码行 */}
          <div className={styles['code-row']}>
            <Form.Item
              name="code"
              rules={[{ required: true, message: t('common.codePl') }]}
              className={styles['code-input']}
            >
              <Input placeholder={t('common.code')} size="large" />
            </Form.Item>
            <Button
              className={styles['send-code-btn']}
              size="large"
              loading={isDisabled}
              disabled={countdown > 0}
              onClick={handleSendCode}
            >
              {countdown > 0 ? `${countdown}s` : t('login.sendCode')}
            </Button>
          </div>

          {/* 密码 */}
          <Form.Item
            name="password"
            rules={[
              { required: true, message: t('login.passwordPl') },
              { min: 6, message: t('login.passwordthen6') }
            ]}
          >
            <Input.Password placeholder={t('login.password')} size="large" />
          </Form.Item>

          {/* 确认密码 */}
          <Form.Item
            name="confirmPassword"
            rules={[
              { required: true, message: t('login.confirmPasswordPl') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('login.confirmPassword')} size="large" />
          </Form.Item>

          {/* 协议同意 */}
          <div className={styles['agreement']}>
            <Checkbox
              checked={checked}
              onChange={changeCheck}
              style={{ marginRight: 5 }}
            />
            <span>
              {t('login.reviewedAagree')}
              <a href="#" className={styles['privacy-link']}> {t('login.PrivacyAgreement')}</a>.
            </span>
          </div>

          {/* 提交按钮 */}
          <Form.Item>
            <Button type="primary" htmlType="submit" className={styles['submit-btn']} size="large">
              {t('login.submit')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Register;
